import React, { Component } from 'react';
import styled, { css } from 'styled-components';
import * as tokens from '@trp-rps-rps-shared-web/rps-shared-web-styles';
import { ErrorBanner, Link } from '@trp-rps-rps-shared-web/rps-shared-web-components';
import {
  formatAmountWithoutDecimals,
  getDateObject,
  formatDate,
  getFormattedCurrentDate,
} from '../../../utils/helpers';
import { accountModalViews, postActions } from '../../../utils/constants';
import { keyDown } from '../../../utils/helpers';

interface ContainerProps {
  disabled?: boolean;
  page?: string;
  isTrpAccount?: boolean;
}

const StyledLink = styled.a`
  text-decoration: none;
  &:hover,
  &:focus {
    text-decoration: none;
  }
`;

const StyledOuterContainer = styled.div<ContainerProps>`
  position: relative;
  width: 100%;
  margin-bottom: 8px;
  border-radius: ${props => (props.page === 'strategyBalance' ? '0' : '4px')};
  border: solid 1px ${props => (props.isTrpAccount ? 'none' : tokens.ColorWhite200)};
  padding: ${props => (props.isTrpAccount || props.page === 'strategyBalance' ? '12px 0px 4px 0px' : '24px')};
  ${(props: any) =>
    props.showOverlay &&
    css`
      border: solid 1px ${tokens.ColorBrandInteractiveBlueHover};
      box-shadow: 0 24px 24px 0 rgba(45, 57, 64, 0.08), 0 0 12px 0 rgba(45, 57, 64, 0.12);
      transform: translateY(0);
    `}
  ${(props: any) =>
    props.page === 'strategyBalance' &&
    css`
      :nth-of-type(2),
      :nth-of-type(4) {
        border-top: 1px solid ${tokens.ColorBrandBrightBlue};
      }
      border-right: none;
      border-left: none;
      border-bottom: none;
      margin-bottom: 0;
      box-shadow: none;
      min-height: auto;
    `}
`;

const StyledContainer = styled.div<ContainerProps>`
  width: 100%;
  border: none;
  background-color: ${tokens.ColorWhite000};
  display: flex;
  cursor: pointer;
  font-family: Nimbus Sans Regular;
  font-size: 16px;
  color: ${tokens.ColorGrey600};
  ${(props: any) =>
    props.disabled &&
    css`
      color: ${tokens.ColorGrey300};
    `}
`;

const StyledHoverableSection = styled.div<ContainerProps>`
  display: flex;
  justify-content: space-between;
  flex-grow: 1;
  @media (max-width: 767px) {
    flex-direction: column;
  }
  ${(props: any) =>
    props.page === 'strategyBalance' &&
    css`
      padding: 0 16px 16px 0;
    `}
`;

const AccountInfoText = css`
  font-family: Nimbus Sans Regular;
  font-size: 16px;
  line-height: 1.38;
  color: ${tokens.ColorGrey600};
`;

const StyledAccountName = styled.div<ContainerProps>`
  font-family: Nimbus Sans Bold;
  font-size: 16px;
  line-height: 1.38;
  padding-right: 16px;
  color: ${props => (props.isTrpAccount ? tokens.ColorGrey800 : tokens.ColorBrandInteractiveBlue)};
  ${(props: any) =>
    props.disabled &&
    css`
      color: ${tokens.ColorGrey300};
    `}
  ${(props: any) =>
    props.page === 'strategyBalance' &&
    !props.disabled &&
    css`
      color: ${tokens.ColorGrey700};
    `}
`;

const StyledAccountBalance = styled.div<ContainerProps>`
  font-family: Nimbus Sans Bold;
  font-size: 16px;
  line-height: 1.38;
  color: ${tokens.ColorGrey800};
  ${(props: any) =>
    props.disabled &&
    css`
      color: ${tokens.ColorGrey300};
    `}
`;

const StyledAccountInfo = styled.div`
  @media (max-width: 767px) {
    margin-bottom: 8px;
  }
`;

const StyledAccountType = styled.div`
  ${AccountInfoText}
`;

const StyledAsOfDate = styled.div`
  ${AccountInfoText}
`;

const StyledBalanceContainer = styled.div`
  text-align: right;
  @media (max-width: 767px) {
    text-align: left;
  }
`;

const StyledNew = styled.span`
  color: ${tokens.ColorFunctionalRed900};
`;

const StyledYodleeErrorContainer = styled.div`
  margin: -16px 0px 0px 0px;
`;

const StyledError = styled.span`
  color: ${tokens.ColorFunctionalRed500};
`;

class AccountCard extends Component<any> {
  showSelectedAccountInfoInEdit = (account: any) => {
    const { setCurrentView, setSelectedAccount, data } = this.props;
    setSelectedAccount(data);
    setCurrentView(accountModalViews.ADD_ACCOUNT);
  };
  props: any;

  handleKeyPress = (e: any) => {
    const { onClick } = this.props;
    if (e.nativeEvent.code === 'Enter') {
      onClick();
    }
  };

  render(): JSX.Element {
    const { page, onClick, isTrpAccount, participantName, spouseName, id } = this.props;
    const {
      name,
      displayType,
      accountHolder,
      balance,
      amount,
      asOfDate,
      includeInScenario,
      action,
      hasYodleeError,
      key,
    } = this.props.data;

    let flooredBalance = formatAmountWithoutDecimals(Math.floor(balance));

    return (
      <React.Fragment>
        <StyledLink id={`card-` + id + `-link`} onClick={onClick} tabIndex={0} onKeyPress={this.handleKeyPress}>
          <StyledOuterContainer id={`card-` + id + `-outer-container`} page={page} isTrpAccount={isTrpAccount}>
            <StyledContainer id={`card-` + id + `-container`} disabled={!includeInScenario}>
              <StyledHoverableSection id={`card-` + id + `-hoverable-section`} tabIndex={0} page={page}>
                <StyledAccountInfo id={`card-` + id + `-account-info`}>
                  <StyledAccountName
                    id={`card-` + id + `-account-name`}
                    disabled={!includeInScenario}
                    page={page}
                    isTrpAccount={isTrpAccount}
                  >
                    {name}
                    {action === postActions.ADD ? <StyledNew id={`card-` + id + `-new`}>&nbsp;new!</StyledNew> : ''}
                  </StyledAccountName>
                  <StyledAccountType id={`card-` + id + `-account-type`}>
                    {displayType ? displayType === 'Hsa' ? 'Investment Balance' : displayType : <StyledError id={`card-` + id + `-error`}>Error</StyledError>} |&nbsp;
                    {accountHolder ? <span className="blocked_data">{accountHolder}</span> : null}
                    {participantName ? <span className="blocked_data">{participantName}</span> : null}
                    {spouseName ? <span className="blocked_data">{spouseName}</span> : null}
                  </StyledAccountType>
                </StyledAccountInfo>
                <StyledBalanceContainer id={`card-` + id + `-balance-container`}>
                  <StyledAccountBalance id={`card-` + id + `-account-balance`} disabled={!includeInScenario}>
                    {flooredBalance}
                  </StyledAccountBalance>
                  <StyledAsOfDate id={`card-` + id + `-as-of-date`}>
                    {page === 'accountCard' && 'As of '}{asOfDate ? formatDate(getDateObject(asOfDate)) : getFormattedCurrentDate()}
                  </StyledAsOfDate>
                </StyledBalanceContainer>
              </StyledHoverableSection>
            </StyledContainer>
          </StyledOuterContainer>
        </StyledLink>

        {hasYodleeError && (
          <StyledYodleeErrorContainer id={`card-` + id + `-yodlee-error`}>
            <ErrorBanner id={`card-` + id + `-error-banner`}>
              This account will not be included in your balances and projections until the errors are corrected.
              <Link
                id={`card-` + id + `-link`}
                linkLabel={'Review account details.'}
                onClick={this.showSelectedAccountInfoInEdit}
                tabIndex={0}
                onKeyPress={(e: React.KeyboardEvent<HTMLDivElement>) => keyDown(e, this.showSelectedAccountInfoInEdit)}
                color={tokens.ColorBrandInteractiveBlue}
                hoverColor={tokens.ColorBrandInteractiveBlueHover}
                customStyles={'display: inline; text-decoration: underline; margin-left: 4px;'}
              />
            </ErrorBanner>
          </StyledYodleeErrorContainer>
        )}
      </React.Fragment>
    );
  }
}

export default AccountCard;
